import { useEffect, useState } from "react";
import Table from "@/components/Table/Table";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import type { StatusTone } from "@/components/StatusBadge/StatusBadge";
import RowActionsMenu from "@/components/ActionCells/RowActionsMenu";
import type { RowAction } from "@/components/ActionCells/RowActionsMenu";
import Eye from "@/components/SVGComponents/Eye";
import MailIcon from "@/components/SVGComponents/MailIcon";
import ClockIcon from "@/components/SVGComponents/ClockIcon";
import SearchIcon from "@/components/SVGComponents/SearchIcon";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import DownloadIcon from "@/components/SVGComponents/DownloadIcon";
import Button from "@/components/Button/Button";
import Pagination from "@/components/Table/Pagination";
import ExportConfirmationModal from "@/components/ExportConfirmationModal/ExportConfirmationModal";
import type { Column } from "@/types/common";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getPayments } from "@/store/slices/paymentSlice";
import paymentService from "@/services/api/payments";
import type { Payment } from "@/services/api/payments";
import { captalizeEachWord } from "@/lib/utils";
import { getDisplayName } from "@/lib/user";
import { formatINR } from "@/lib/currency";
import { formatDate } from "@/lib/dateFormat";
import { formatRoom } from "@/pages/Members/lib/memberFormat";
import toast from "@/lib/toast";
import PaymentCard from "./components/PaymentCard";
import styles from "./Payments.module.scss";



// The screenshot's filter pills (All/Active/Past due/Paused) were
// removed for now, per the user — subscription-lifecycle states with no
// backing field on this endpoint anyway (same reasoning as the stat
// cards above). Revisit alongside `STATUS_TABS` if/when they come back.

// `paymentType` is confirmed nullable on a real row (see `Payment`'s own
// comment in services/api/payments.ts and docs/decisions.md #90 — this
// used to call `.toLowerCase()` straight on it, which crashed the whole
// table via the render error boundary the moment a null one loaded).
// Every field read below is guarded the same defensive way, on the same
// reasoning: this endpoint has already shown it doesn't honor its own
// apparent non-null fields, so nothing here is trusted without a check
// first, confirmed-nullable-so-far or not.
const PAYMENT_TYPE_LABELS: Record<string, string> = { upi: "UPI", card: "Card", emandate: "eMandate" };
const formatPaymentType = (type: string | null | undefined) => {
  if (!type) return "—";
  return PAYMENT_TYPE_LABELS[type.toLowerCase()] ?? captalizeEachWord(type);
};

const getPaymentStatusTone = (status: string | null | undefined): StatusTone => {
  const normalized = (status ?? "").toUpperCase();
  if (normalized === "SUCCESS") return "success";
  if (normalized === "FAILED") return "danger";
  if (normalized === "PENDING") return "warning";
  return "neutral";
};

const formatPaymentStatus = (status: string | null | undefined) => (status ? status.toUpperCase() : "UNKNOWN");

const USER_TYPE_LABEL: Record<string, string> = { FOUNDER: "Founder", INVESTOR: "Investor" };
const formatUserType = (userType: string | null | undefined) => (userType ? (USER_TYPE_LABEL[userType] ?? userType) : "—");

// Not wired up yet — no payment-detail page, receipt-resend, or manual
// refund-processing endpoint is confirmed to exist. Each action just
// confirms the click landed, same convention as Members.tsx's own
// row actions.
const getRowActions = (payment: Payment): RowAction[] => {
  const name = getDisplayName(payment, { fallbackLabel: "Payer" });
  return [
    {
      key: "view",
      label: "View payment",
      icon: <Eye width={16} height={16} strokeWidth={1.75} />,
      onClick: () => toast.warning(`Viewing ${name}'s payment isn't wired up yet.`),
    },
    {
      key: "receipt",
      label: "Resend receipt",
      icon: <MailIcon />,
      onClick: () => toast.warning(`Resending a receipt to ${name} isn't wired up yet.`),
    },
    {
      key: "refund",
      label: "Process refund",
      icon: <ClockIcon />,
      dividerBefore: true,
      onClick: () => toast.warning(`Processing a refund for ${name} isn't wired up yet.`),
    },
  ];
};

const ROWS_PER_PAGE = 10;

const Payments = () => {
  const dispatch = useAppDispatch();
  const { payments, totalPayments, isFetching } = useAppSelector((state) => state.payment);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [pageSelected, setPageSelected] = useState(1);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  useEffect(() => {
    setPageSelected(1);
  }, [debouncedSearch]);

  useEffect(() => {
    dispatch(
      getPayments({
        search: debouncedSearch || undefined,
        pageNo: pageSelected,
        perPage: ROWS_PER_PAGE,
      }),
    );
  }, [dispatch, debouncedSearch, pageSelected]);

  usePageSubtitle(
    isFetching ? "Loading payments…" : `${totalPayments.toLocaleString("en-IN")} subscriptions`,
  );

  const columns: Column<Payment>[] = [
    {
      name: "Member",
      width: "20%",
      cell: (row) => (
        <div className={styles.member}>
          <span className={styles.memberName}>{getDisplayName(row, { fallbackLabel: "Payer" })}</span>
          <span className={styles.memberSubtitle}>{formatUserType(row.userType)}</span>
        </div>
      ),
    },
    {
      name: "Room",
      width: "13%",
      center: true,
      cell: (row) => {
        const room = formatRoom(row.userCategory);
        return room ? (
          <StatusBadge label={room} tone="neutral" className={styles.roomBadge} />
        ) : (
          <span>—</span>
        );
      },
    },
    { name: "Amount", width: "10%", selector: (row) => formatINR(row?.amount) },
    {
      name: "Started",
      width: "10%",
      selector: (row) => formatDate(row?.subscriptionStartedAt || row?.createdAt),
    },
    {
      name: "Renews",
      width: "11%",
      selector: (row) => formatDate(row?.renewsAt),
    },
    { name: "Payment method", width: "12%", selector: (row) => formatPaymentType(row?.paymentType) },
    {
      name: "Auto-renew",
      width: "13%",
      cell: (row) => <span>{row?.autoRenewal ? "On" : "Off"}</span>,
    },
    {
      name: "Status",
      width: "10%",
      cell: (row) => (
        <StatusBadge label={formatPaymentStatus(row?.paymentStatus)} tone={getPaymentStatusTone(row?.paymentStatus)} />
      ),
    },
  ];

  return (
    <div className={styles.page}>
      {/* Desktop View (100% strictly preserved existing UI) */}
      <div className={styles.desktopView}>
        <Table<Payment>
          columns={columns}
          data={payments}
          progressPending={isFetching}
          header={{
            search,
            setSearch,
            searchPlaceholder: "Search subscribers",
            onExport: () => setIsExportModalOpen(true),
          }}
          paginationProps={{
            isPagination: true,
            pageSelected,
            totalCount: totalPayments,
            rowsPerPageValue: ROWS_PER_PAGE,
            setPageSelected,
          }}
        />
      </div>

      {/* Mobile View (Simple, sober card-based UI for mobile screens <= 768px only) */}
      <div className={styles.mobileView}>
        {/* Mobile Search & Export Controls */}
        <div className={styles.mobileControlsRow}>
          <div className={styles.mobileSearchBox}>
            <SearchIcon className={styles.mobileSearchIcon} />
            <input
              type="text"
              placeholder="Search subscribers"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <CrossIcon
                onClick={() => setSearch("")}
                withBg={false}
                height={12}
                width={12}
                strokeWidth={2}
                className={styles.mobileCrossIcon}
              />
            )}
          </div>

          <Button
            outlined
            onClick={() => setIsExportModalOpen(true)}
            className={styles.mobileExportBtn}
          >
            <DownloadIcon />
            <span>EXPORT CSV</span>
          </Button>
        </div>

        {/* Mobile Card List */}
        <div className={styles.mobileCardList}>
          {isFetching ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className={styles.mobileSkeletonCard}>
                <div className={styles.skeletonHeader}>
                  <div className={styles.skeletonAvatar} />
                  <div className={styles.skeletonTextGroup}>
                    <div className={styles.skeletonLineShort} />
                    <div className={styles.skeletonLineLong} />
                  </div>
                </div>
                <div className={styles.skeletonAmount} />
                <div className={styles.skeletonDivider} />
                <div className={styles.skeletonGrid}>
                  <div className={styles.skeletonLine} />
                  <div className={styles.skeletonLine} />
                  <div className={styles.skeletonLine} />
                  <div className={styles.skeletonLine} />
                </div>
              </div>
            ))
          ) : payments.length === 0 ? (
            <div className={styles.mobileEmptyState}>
              <h4 className={styles.mobileEmptyTitle}>No payments found</h4>
              <p className={styles.mobileEmptyCaption}>
                {search
                  ? "No subscriber payments match your search query."
                  : "No payment records available."}
              </p>
            </div>
          ) : (
            payments.map((payment) => (
              <PaymentCard
                key={payment.id}
                payment={payment}
                actions={getRowActions(payment)}
                paymentTypeLabel={formatPaymentType(payment.paymentType)}
                statusTone={getPaymentStatusTone(payment.paymentStatus)}
                statusLabel={formatPaymentStatus(payment.paymentStatus)}
                userTypeLabel={formatUserType(payment.userType)}
              />
            ))
          )}
        </div>

        {/* Mobile Pagination */}
        {totalPayments > ROWS_PER_PAGE && (
          <div className={styles.mobilePaginationContainer}>
            <Pagination
              pageSelected={pageSelected}
              totalCount={totalPayments}
              rowsPerPageValue={ROWS_PER_PAGE}
              setPageSelected={setPageSelected}
            />
          </div>
        )}
      </div>

      <ExportConfirmationModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        fallbackFileName="payments"
        fetchExportFile={() => paymentService.exportPayments({ search: debouncedSearch || undefined })}
      />
    </div>
  );
};

export default Payments;
